import { Trans } from '@lingui/react/macro';
import { Paper, PaperProps } from '@mui/material';
import Typography from '@mui/material/Typography';

import memoize from 'lodash/memoize';

import { TestIds } from '@watershed/shared-universal/utils/testUtils';

const TRUNCATED_MESSAGE_CLASS = 'truncated-message';

/**
 * Custom paper component for Autocomplete that shows a message if the options are truncated.
 * This depends on the max number of allowed options, which MUI won't pass in slot props, so
 * we create the component with a memoized function that takes the maxItems as an argument.
 */
export const AutocompletePaper = memoize(
  ({ maxItems }: { maxItems: number }) =>
    ({ children, ...props }: PaperProps) => {
      return (
        <Paper
          {...props}
          sx={{
            [`.${TRUNCATED_MESSAGE_CLASS}`]: {
              display: 'none',
            },
            [`&:has(li:nth-of-type(${maxItems})) .${TRUNCATED_MESSAGE_CLASS}`]:
              {
                display: 'block',
              },
          }}
        >
          {children}
          <Typography
            role="note"
            variant="body3"
            className={TRUNCATED_MESSAGE_CLASS}
            data-testid={TestIds.AutocompleteCount}
            sx={{
              color: (theme) => theme.palette.grey70,
              padding: 1,
              borderTop: (theme) => `1px solid ${theme.palette.grey20}`,
            }}
          >
            <Trans>Showing first {maxItems} values</Trans>
          </Typography>
        </Paper>
      );
    },
  (props) => props.maxItems
);
